import { workflowActionAllowedForAnyRole } from '../services/workflowRuntimeService';

/** Clé action workflow (milestone_template_actions). */
export const CRM_STUDIO_ACTION_KEY = 'open_crm_studio';

/** Valeurs par défaut (seed migration) si la ligne Supabase est absente. */
export const CRM_STUDIO_DEFAULT_SETTINGS = {
  baseUrl: 'https://ifbegin.move-logic.com/crmstudio',
  isEnabled: true,
  allowedRoleTypes: ['administrator', 'dentist_supplier'],
  allowedPhaseKeys: ['design', 'production', 'quality_check', 'shipping'],
};

function normalizePhaseName(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** @param {Record<string, unknown> | null | undefined} row */
export function normalizeCrmStudioSettings(row) {
  if (!row) {
    return { ...CRM_STUDIO_DEFAULT_SETTINGS };
  }
  const baseUrl =
    typeof row.baseUrl === 'string'
      ? row.baseUrl.trim().replace(/\/+$/, '')
      : String(row.base_url ?? '')
          .trim()
          .replace(/\/+$/, '');

  return {
    baseUrl,
    isEnabled: row.isEnabled !== false && row.is_enabled !== false,
    allowedRoleTypes: Array.isArray(row.allowedRoleTypes)
      ? row.allowedRoleTypes.filter(Boolean)
      : Array.isArray(row.allowed_role_types)
        ? row.allowed_role_types.filter(Boolean)
        : [],
    allowedPhaseKeys: Array.isArray(row.allowedPhaseKeys)
      ? row.allowedPhaseKeys.filter(Boolean)
      : Array.isArray(row.allowed_phase_keys)
        ? row.allowed_phase_keys.filter(Boolean)
        : [],
  };
}

/**
 * @param {ReturnType<typeof normalizeCrmStudioSettings>} settings
 */
export function isCrmStudioConfigured(settings) {
  const s = normalizeCrmStudioSettings(settings);
  return s.isEnabled && Boolean(s.baseUrl);
}

/**
 * Phase timeline « Conception » (groupe 1, ordre 3).
 * @param {{ group?: string|number, phase_order?: number, name?: string, phase_name?: string } | null} phase
 */
export function isDesignPhaseTimelineItem(phase) {
  if (!phase) return false;
  const group = String(phase.group ?? '');
  const order = Number(phase.phase_order);
  if (group === '1' && order === 3) return true;
  const n = normalizePhaseName(phase.name ?? phase.phase_name);
  return n.includes('phase de conception') || (n.includes('conception') && !n.includes('soumission'));
}

function getPhaseItems(phases) {
  if (!phases) return [];
  if (Array.isArray(phases)) return phases;
  return phases.items ?? [];
}

/**
 * Fenêtre d’affichage : current_phase design ou phase Conception in_progress.
 */
export function isInDesignPhaseWindow(currentPhase, phases) {
  if (currentPhase != null && String(currentPhase).trim() === 'design') return true;
  return getPhaseItems(phases).some(
    (p) => isDesignPhaseTimelineItem(p) && p.status === 'in_progress',
  );
}

function workflowGrantsCrmStudioAccess({ userRoleTypes, phases, templateActionsByTemplateId }) {
  const designPhases = getPhaseItems(phases).filter(isDesignPhaseTimelineItem);

  for (const phase of designPhases) {
    for (const m of phase.milestones || []) {
      const tid = m.milestone_template_id;
      if (!tid) continue;
      const actions = templateActionsByTemplateId?.[tid] || [];
      const studioAction = actions.find((a) => a.action_key === CRM_STUDIO_ACTION_KEY);
      if (
        studioAction &&
        workflowActionAllowedForAnyRole(userRoleTypes, studioAction.allowed_role_types)
      ) {
        return true;
      }
    }
  }
  return false;
}

function hasAnyWorkflowCrmStudioBinding(templateActionsByTemplateId) {
  if (!templateActionsByTemplateId) return false;
  return Object.values(templateActionsByTemplateId).some((rows) =>
    rows?.some((r) => r.action_key === CRM_STUDIO_ACTION_KEY),
  );
}

/**
 * @param {{
 *   patientId?: string | null,
 *   caseId?: string | null,
 *   settings?: ReturnType<typeof normalizeCrmStudioSettings> | null,
 * }} params
 * @returns {string | null}
 */
export function buildCrmStudioUrl({ patientId, caseId, settings }) {
  const pid = patientId != null ? String(patientId).trim() : '';
  const cid = caseId != null ? String(caseId).trim() : '';
  if (!pid || !cid) return null;

  const s = normalizeCrmStudioSettings(settings);
  if (!isCrmStudioConfigured(s)) return null;
  return `${s.baseUrl}/${pid}/${cid}`;
}

/**
 * Repli historique (Intégrations : rôles + phases).
 * @param {{
 *   settings?: ReturnType<typeof normalizeCrmStudioSettings> | null,
 *   userRoleTypes?: string[],
 *   currentPhase?: string | null,
 * }} params
 */
export function shouldShowCrmStudioButton({ settings, userRoleTypes = [], currentPhase }) {
  const s = normalizeCrmStudioSettings(settings);
  if (!isCrmStudioConfigured(s)) return false;
  if (!s.allowedRoleTypes.length || !s.allowedPhaseKeys.length) return false;

  const roles = Array.isArray(userRoleTypes) ? userRoleTypes : [];
  if (!s.allowedRoleTypes.some((r) => roles.includes(r))) return false;

  const phase = currentPhase != null ? String(currentPhase).trim() : '';
  if (!phase || !s.allowedPhaseKeys.includes(phase)) return false;

  return true;
}

/**
 * Visibilité bouton Studio 3D (header + agrégat cas).
 * @param {{
 *   settings?: ReturnType<typeof normalizeCrmStudioSettings> | null,
 *   userRoleTypes?: string[],
 *   currentPhase?: string | null,
 *   phases?: { items?: Array } | Array | null,
 *   templateActionsByTemplateId?: Record<string, Array<{ action_key: string, allowed_role_types?: string[] }>>,
 * }} params
 */
export function shouldShowCrmStudioForCase({
  settings,
  userRoleTypes = [],
  currentPhase,
  phases,
  templateActionsByTemplateId = {},
}) {
  if (!isCrmStudioConfigured(settings)) return false;
  if (!isInDesignPhaseWindow(currentPhase, phases)) return false;

  if (
    workflowGrantsCrmStudioAccess({
      userRoleTypes,
      phases,
      templateActionsByTemplateId,
    })
  ) {
    return true;
  }

  if (!hasAnyWorkflowCrmStudioBinding(templateActionsByTemplateId)) {
    return shouldShowCrmStudioButton({ settings, userRoleTypes, currentPhase });
  }

  return false;
}

/**
 * Scans STL requis avant ouverture du studio (arcades sup. / inf.).
 * @param {Array<{ category?: string, filePath?: string }>} files
 * @returns {{ ok: boolean, missingLabel: string | null }}
 */
export function getCrmStudioScanReadiness(files) {
  const list = Array.isArray(files) ? files : [];
  const upperScan = list.find(
    (f) => f.category === 'scans' && f.filePath?.includes('-upper.stl'),
  );
  const lowerScan = list.find(
    (f) => f.category === 'scans' && f.filePath?.includes('-lower.stl'),
  );

  if (upperScan && lowerScan) {
    return { ok: true, missingLabel: null };
  }

  const missingLabel =
    !upperScan && !lowerScan
      ? 'les scans maxillaire et mandibulaire sont'
      : !upperScan
        ? 'le scan maxillaire est'
        : 'le scan mandibulaire est';

  return { ok: false, missingLabel };
}

/**
 * Ouvre le Studio 3D dans un nouvel onglet (toast si config ou scans manquants).
 * @param {{
 *   patientId?: string | null,
 *   caseId?: string | null,
 *   settings?: ReturnType<typeof normalizeCrmStudioSettings> | null,
 *   files?: Array,
 *   toastFn?: { error: (msg: string) => void },
 * }} params
 * @returns {boolean}
 */
export function openCrmStudioInNewTab({
  patientId,
  caseId,
  settings,
  files = [],
  toastFn,
}) {
  const url = buildCrmStudioUrl({ patientId, caseId, settings });
  if (!url) {
    toastFn?.error?.(
      'Studio 3D non configuré. Vérifiez l’activation et l’URL dans Administration → Intégrations.',
    );
    return false;
  }

  const { ok, missingLabel } = getCrmStudioScanReadiness(files);
  if (ok) {
    window.open(url, '_blank');
    return true;
  }

  toastFn?.error?.(
    `⚠️ ${missingLabel} manquant(s). Veuillez ajouter les scans avant d'accéder au studio.`,
  );
  return false;
}
